import React, { useState } from 'react';
import { View, TextInput, TouchableOpacity, Text, StyleSheet, type ViewStyle, type TextStyle } from 'react-native';
import PlatformIcon from './PlatformIcon';
import { ORANGE_ACTION } from '../../constants/colors';

export interface SearchBarProps {
  /** 搜索框的占位符文本 */
  placeholder?: string;
  /** 搜索文本值 */
  value: string;
  /** 搜索文本变化回调 */
  onChangeText: (text: string) => void;
  /** 执行搜索的回调 */
  onSearch?: () => void;
  /** 清除搜索的回调 */
  onClear?: () => void;
  /** 点击 Filter 按钮的回调 */
  onFilterPress?: () => void;
  /** 是否显示 Filter 按钮 */
  showFilter?: boolean;
  /** 是否禁用搜索框 */
  disabled?: boolean;
  /** 自定义样式 */
  style?: ViewStyle;
  /** 搜索输入框的自定义样式 */
  inputStyle?: TextStyle;
  /** 搜索图标颜色 */
  iconColor?: string;
  /** 清除按钮的可见性 */
  showClear?: boolean;
}

/**
 * 搜索栏组件
 * 
 * 包含搜索输入框、搜索图标、清除按钮和可选的 Filter 按钮
 * 
 * @example
 * ```tsx
 * <SearchBar
 *   value={searchText}
 *   onChangeText={setSearchText}
 *   onSearch={handleSearch}
 *   onFilterPress={handleOpenFilter}
 *   showFilter
 * />
 * ```
 */
const SearchBar: React.FC<SearchBarProps> = ({
  placeholder = '搜索地点或标题',
  value,
  onChangeText,
  onSearch,
  onClear,
  onFilterPress,
  showFilter = true,
  disabled = false,
  style,
  inputStyle,
  iconColor = ORANGE_ACTION,
  showClear = true,
}) => {
  const [isFocused, setIsFocused] = useState(false);

  const handleClear = () => {
    onChangeText('');
    onClear?.();
  };

  return (
    <View style={[styles.container, style]}>
      <View style={[styles.searchWrap, isFocused && styles.searchWrapFocused]}>
        <View style={styles.searchInner}>
          <TouchableOpacity
            accessible
            accessibilityLabel="执行搜索"
            accessibilityRole="button"
            onPress={onSearch}
            style={styles.searchIcon}
            hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
            disabled={disabled}
          >
            <PlatformIcon name="magnify" size={18} color={iconColor} />
          </TouchableOpacity>

          <TextInput
            style={[styles.searchInput, inputStyle]}
            placeholder={placeholder}
            accessible
            accessibilityLabel="搜索"
            value={value}
            onChangeText={onChangeText}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            returnKeyType="search"
            onSubmitEditing={onSearch}
            clearButtonMode="never"
            underlineColorAndroid="transparent"
            editable={!disabled}
            placeholderTextColor="#9CA3AF"
          />

          {showClear && value.length > 0 ? (
            <TouchableOpacity
              accessible
              accessibilityLabel="清除搜索"
              accessibilityRole="button"
              onPress={handleClear}
              style={styles.searchClearButton}
              hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
              disabled={disabled}
            >
              <PlatformIcon name="close" size={16} color={iconColor} />
            </TouchableOpacity>
          ) : null}
        </View>

        {showFilter && (
          <TouchableOpacity
            style={styles.searchFilterIcon}
            onPress={onFilterPress}
            accessible
            accessibilityLabel="打开筛选"
            accessibilityRole="button"
            hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
            disabled={disabled}
          >
            <PlatformIcon name="tune" size={24} color="#6b7280" />
          </TouchableOpacity>
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#FFFFFF',
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  searchWrap: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  searchWrapFocused: {
    // Can add focus styling here if needed
  },
  searchInner: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    height: 40,
    backgroundColor: '#F3F4F6',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  searchIcon: {
    paddingHorizontal: 4,
    paddingVertical: 8,
    justifyContent: 'center',
    alignItems: 'center',
  },
  searchInput: {
    flex: 1,
    paddingHorizontal: 8,
    paddingVertical: 0,
    fontSize: 14,
    color: '#111827',
    fontWeight: '500',
    outlineStyle: 'none', // 移除聚焦时的黑色边框
  },
  searchClearButton: {
    paddingHorizontal: 4,
    paddingVertical: 8,
    justifyContent: 'center',
    alignItems: 'center',
  },
  searchFilterIcon: {
    paddingHorizontal: 8,
    paddingVertical: 8,
    justifyContent: 'center',
    alignItems: 'center',
  },
});

export default SearchBar;
